import { useEffect, useState } from "react";
import "./onehealth.css";
import DeviceSetup from "./DeviceSetup";
import { getDeviceKey, hasRosterAccess, syncRoster } from "./roster";
import { LangProvider, LanguageSwitcher, useLang } from "./OneHealthContext";
import { LTBrand, PoweredByCarePlix } from "./components";
import GetStarted from "./steps/GetStarted";
import SiteSelect from "./steps/SiteSelect";
import FaceScan from "./steps/FaceScan";
import Pulmo from "./steps/Pulmo";
import Pvtb from "./steps/Pvtb";
import Result from "./steps/Result";

/* Step state machine for the worker-facing flow:
   getstarted → facescan → [pulmo?] → [pvtb?] → result
   The Pulmo (lung) scan and the PVT-B attention test are each gated by a
   per-worker flag — set on the directory record and/or via the manual-entry
   toggles. FaceScan resolves those flags and hands them (plus the shared
   scan_token) to the flow, which routes accordingly. */
/* Dev-only visual harness (?demoResult=1): renders the Result screen with a
   SAMPLE sanitized report so layout/PDF can be checked without a live scan.
   Stripped from production builds — never part of the real flow, and never a
   substitute for real vitals. */
const demoHarness =
  process.env.NODE_ENV !== "production" &&
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("demoResult")
    ? {
        result: {
          ok: true, verdict: "caution", wellbeing: 72, wellbeingStatus: "green",
          employee: { name: "Demo Worker", age: 34, gender: "Male" },
          params: [
            { key: "hr", value: 104, status: "red" },
            { key: "resp", value: 16, status: "green" },
            { key: "spo2", value: 97, status: "green" },
            { key: "bp", band: "average", bandRange: "130–139 / 85–89", status: "amber" },
            { key: "sdnn", value: 48.2, status: "amber" },
            { key: "rmssd", value: 42.5, status: "green" },
            { key: "pnn50", value: 4.1, status: "amber" },
            { key: "cardiacOut", value: 5.6, status: "green" },
            { key: "map", band: "normal", status: "green" },
            { key: "stress", band: "elevated", status: "amber" },
            { key: "hba1c", band: "aboveOptimal", bandRange: "≥ 5.7", status: "amber" },
            { key: "diabRisk", value: 34, status: "green" },
            { key: "bmi", value: 26.4, status: "amber" },
            { key: "bmr", value: 1620, status: "na" },
            { key: "bloodVolume", value: 4900, status: "na" },
            { key: "bodyWater", value: 55.2, status: "na" },
            { key: "bodyFat", value: 24.8, status: "na" },
            { key: "vo2max", value: 38.5, status: "na" },
          ],
        },
        pulmoResult: { score: 81, fevFvc: 0.82, oad: false },
        pvtResult: { mean: 412, lapses: 5, falseStarts: 1, band: "ok", count: 12 },
      }
    : null;

function Flow() {
  const { dir } = useLang();
  const [step, setStep] = useState(demoHarness ? "result" : "getstarted");
  const [result, setResult] = useState(demoHarness ? demoHarness.result : null);
  const [opts, setOpts] = useState({ pulmo: false, cognitive: false, scanToken: null });
  const [pulmoResult, setPulmoResult] = useState(demoHarness ? demoHarness.pulmoResult : null);
  const [pvtResult, setPvtResult] = useState(demoHarness ? demoHarness.pvtResult : null);
  const [scanKey, setScanKey] = useState(0);
  const [showSetup, setShowSetup] = useState(false);

  // Dashboard-linked device: refresh the roster (delta sync — new/changed
  // photos re-embedded, deleted workers purged) once on load.
  useEffect(() => {
    if (hasRosterAccess()) syncRoster().catch((e) => console.warn("roster sync failed:", e?.message));
  }, []);

  const reset = () => {
    setResult(null); setOpts({ pulmo: false, cognitive: false, scanToken: null });
    setPulmoResult(null); setPvtResult(null); setStep("getstarted");
  };

  // After a screening step, decide where to go next given the flags.
  const afterFace = (o) => (o.pulmo ? "pulmo" : o.cognitive ? "pvtb" : "result");
  const afterPulmo = () => (opts.cognitive ? "pvtb" : "result");

  const onScanDone = (payload, o) => {
    setResult(payload);
    setOpts(o);
    setStep(afterFace(o));
  };

  const onPulmoDone = (pr) => { setPulmoResult(pr); setStep(afterPulmo()); };
  const onPvtDone = (pv) => { setPvtResult(pv); setStep("result"); };

  let screen = null;
  switch (step) {
    case "getstarted":
      /* Keyless kiosk → pick a site first so face auth has a roster to match
         against; a device-keyed kiosk goes straight to the scan. */
      screen = <GetStarted onStart={() => { setScanKey((k) => k + 1); setStep(getDeviceKey() ? "facescan" : "site"); }} />;
      break;
    case "site":
      screen = <SiteSelect onDone={() => setStep("facescan")} onBack={() => setStep("getstarted")} />;
      break;
    case "facescan":
      screen = <FaceScan key={"scan" + scanKey} onDone={onScanDone} onBack={() => setStep("getstarted")} />;
      break;
    case "pulmo":
      screen = <Pulmo scanToken={opts.scanToken} employee={result?.employee} onDone={onPulmoDone} />;
      break;
    case "pvtb":
      screen = <Pvtb age={result?.employee?.age} onDone={onPvtDone} />;
      break;
    case "result":
      screen = <Result result={result} pulmoResult={pulmoResult} pvtResult={pvtResult} onRestart={reset} />;
      break;
    default:
      screen = null;
  }

  return (
    <div className="oh-stage" dir={dir}>
      <div className="oh-device">
        <div className="oh-brandtop">
          <LTBrand />
          <div className="oh-brandtop-right">
            <PoweredByCarePlix />
            <LanguageSwitcher />
          </div>
        </div>
        <div className="oh-screen">{screen}</div>
        {step === "getstarted" && (
          <button className="oh-settings-btn" onClick={() => setShowSetup(true)}>
            <span aria-hidden="true">⚙</span> Settings
          </button>
        )}
        {showSetup && <DeviceSetup onClose={() => setShowSetup(false)} />}
      </div>
    </div>
  );
}

export default function OneHealth() {
  return (
    <LangProvider>
      <Flow />
    </LangProvider>
  );
}
